import React, { useState, useEffect } from 'react';
import { 
  MagnifyingGlassIcon,
  BookmarkIcon,
  EyeIcon,
  ChatBubbleLeftRightIcon,
  HandThumbUpIcon,
  PencilIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  TrashIcon,
  MapPinIcon,
  PaperAirplaneIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { 
  BookmarkIcon as BookmarkSolidIcon,
  HandThumbUpIcon as HandThumbUpSolidIcon, 
  MapPinIcon as MapPinSolidIcon,
  StarIcon as StarSolidIcon 
} from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import intranetApi from '../../services/intranetApi';
import api from '../../utils/api';
import EditPost from './EditPost';
import EditComment from './EditComment';
import PostLikes from './PostLikes';
import PostBookmark from './PostBookmark';
import MentionModal from '../common/MentionModal';
import { renderContentWithMentions } from '../../utils/mentions';
import { getIconComponent } from '../../utils/iconMapping';

const BookmarkedPosts = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [showEditCommentModal, setShowEditCommentModal] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [totalBookmarks, setTotalBookmarks] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    category_id: '',
    limit: 20,
    offset: 0
  });
  const [hasMore, setHasMore] = useState(true);
  
  // Comment-related state
  const [expandedComments, setExpandedComments] = useState(new Set());
  const [postComments, setPostComments] = useState({});
  const [loadingComments, setLoadingComments] = useState(new Set());
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [activeCommentPostId, setActiveCommentPostId] = useState(null);

  // Track which posts have had their views tracked to avoid duplicate tracking
  const [trackedViews, setTrackedViews] = useState(new Set());
  const [expandedPosts, setExpandedPosts] = useState(new Set());

  useEffect(() => {
    fetchBookmarkedPosts();
    fetchCategories();
    fetchCurrentUser();
  }, [filters]);

  const fetchCurrentUser = async () => {
    try {
      const userResponse = await api.get('/auth/me');
      setCurrentUser(userResponse.data.user);
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  const fetchBookmarkedPosts = async (append = false) => {
    try {
      setLoading(!append);
      setError(null);

      const response = await intranetApi.posts.getUserBookmarks(filters);
      const newPosts = response.data.posts || [];

      if (append) {
        setPosts(prev => [...prev, ...newPosts]);
      } else {
        setPosts(newPosts);
      }

      setTotalBookmarks(response.data.total_bookmarks || 0);
      setHasMore(newPosts.length === filters.limit);
    } catch (err) {
      console.error('Error fetching bookmarked posts:', err);
      setError('Failed to load bookmarked posts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await intranetApi.config.getPostCategories();
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, offset: 0 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, offset: 0 }));
  };

  const loadMore = () => {
    setFilters(prev => ({ ...prev, offset: prev.offset + prev.limit }));
    fetchBookmarkedPosts(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setShowEditModal(true);
  };

  const handlePostUpdated = (updatedPost) => {
    setPosts(prev => prev.map(post => 
      post.id === updatedPost.id ? updatedPost : post
    ));
    setShowEditModal(false);
    setEditingPost(null);
  };

  const canUserEditPost = (post) => {
    return currentUser && post.author_id === currentUser.id;
  };

  const handleToggleLike = async (postId, currentLikeStatus) => {
    try {
      const response = await intranetApi.posts.toggleLike(postId);
      
      if (response.data.success) {
        setPosts(prev => prev.map(post => 
          post.id === postId 
            ? { 
                ...post, 
                user_liked: response.data.user_liked,
                like_count: response.data.like_count 
              }
            : post
        ));
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      toast.error('Failed to toggle like');
    }
  };

  const handleToggleBookmark = async (postId, isBookmarked, bookmarkCount) => {
    // Update the post in the list
    setPosts(prev => prev.map(post => 
      post.id === postId 
        ? { 
            ...post, 
            user_bookmarked: isBookmarked,
            bookmark_count: bookmarkCount 
          }
        : post
    ));

    // If post was unbookmarked, remove it from the list after a short delay
    if (!isBookmarked) {
      setTimeout(() => {
        setPosts(prev => prev.filter(post => post.id !== postId));
        setTotalBookmarks(prev => prev - 1);
      }, 500);
    }
  };

  // Comment-related functions
  const toggleComments = async (postId) => {
    const newExpanded = new Set(expandedComments);
    
    if (newExpanded.has(postId)) {
      newExpanded.delete(postId);
      setExpandedComments(newExpanded);
    } else {
      newExpanded.add(postId);
      setExpandedComments(newExpanded);
      
      if (!postComments[postId]) {
        await fetchComments(postId);
      }
    }
  };

  const fetchComments = async (postId) => {
    const newLoadingComments = new Set(loadingComments);
    newLoadingComments.add(postId);
    setLoadingComments(newLoadingComments);

    try {
      const response = await intranetApi.comments.getComments(postId);
      setPostComments(prev => ({
        ...prev,
        [postId]: response.data.comments || []
      }));
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('Failed to load comments');
    } finally {
      newLoadingComments.delete(postId);
      setLoadingComments(newLoadingComments);
    }
  };

  const openCommentModal = (postId) => {
    setActiveCommentPostId(postId);
    setShowCommentModal(true);
  };

  const closeCommentModal = () => {
    setShowCommentModal(false);
    setActiveCommentPostId(null);
  };

  const submitComment = async (content) => {
    if (!activeCommentPostId) return;

    try {
      const response = await intranetApi.comments.addComment(activeCommentPostId, { content });
      
      if (response.data.success) {
        setPostComments(prev => ({
          ...prev,
          [activeCommentPostId]: [response.data.comment, ...(prev[activeCommentPostId] || [])]
        }));
        
        setPosts(prev => prev.map(post => 
          post.id === activeCommentPostId 
            ? { ...post, comment_count: (post.comment_count || 0) + 1 }
            : post
        ));
        
        if (!expandedComments.has(activeCommentPostId)) {
          setExpandedComments(prev => new Set([...prev, activeCommentPostId]));
        }
        
        toast.success('Comment added successfully!');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
      throw error;
    }
  };

  const formatCommentDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const canUserEditComment = (comment) => {
    if (!currentUser) return false;
    // User can edit their own comments
    return currentUser.id === comment.author_id;
  };

  const handleEditComment = (comment) => {
    setEditingComment(comment);
    setShowEditCommentModal(true);
  };

  if (loading && posts.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <BookmarkSolidIcon className="h-6 w-6 text-yellow-600 mr-2" />
            My Bookmarked Posts
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {totalBookmarks} bookmarked post{totalBookmarks !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search bookmarked posts..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </form>

          {/* Category Filter */}
          <select
            value={filters.category_id}
            onChange={(e) => handleFilterChange('category_id', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">All Categories</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.display_label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => fetchBookmarkedPosts()} 
            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors duration-200"
          >
            Retry
          </button>
        </div>
      )}

      {/* Posts List */}
      {posts.length === 0 && !loading ? (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-8 text-center">
          <div className="max-w-md mx-auto">
            <div className="h-24 w-24 mx-auto bg-yellow-100 rounded-full flex items-center justify-center mb-4">
              <BookmarkIcon className="h-12 w-12 text-yellow-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Bookmarked Posts</h3>
            <p className="text-gray-500 mb-6">
              {filters.search || filters.category_id 
                ? 'No bookmarked posts match your current filters. Try adjusting your search criteria.'
                : 'You haven\'t bookmarked any posts yet. Start bookmarking posts you want to reference later!'
              }
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {posts.map((post) => (
            <div key={post.id} className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              {/* Post Header */}
              <div className="p-6 pb-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-indigo-500 flex items-center justify-center text-white font-medium">
                        {post.author_name ? post.author_name.charAt(0).toUpperCase() : 'U'}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{post.author_name || 'Unknown User'}</p>
                      <p className="text-xs text-gray-500">{formatDate(post.created_at)}</p>
                      <p className="text-xs text-yellow-600">
                        Bookmarked {formatDate(post.bookmarked_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {post.is_pinned && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Pinned
                      </span>
                    )}
                    {post.is_required_reading && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Required Reading
                      </span>
                    )}
                  </div>
                </div>

                {/* Category */}
                {post.category_label && (
                  <div className="mt-3">
                    <span 
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white"
                      style={{ backgroundColor: post.category_color || '#6B7280' }}
                    >
                      {(() => {
                        const IconComponent = getIconComponent(post.category_icon, 'category');
                        return <IconComponent className="h-3 w-3 mr-1" />;
                      })()}
                      {post.category_label}
                    </span>
                  </div>
                )}

                {/* Post Content */}
                <div className="mt-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{post.title}</h4>
                  <div className="text-gray-700 text-sm leading-relaxed">
                    {expandedPosts.has(post.id) ? (
                      <>
                        {renderContentWithMentions(post.content)}
                        <button 
                          onClick={() => setExpandedPosts(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(post.id);
                            return newSet;
                          })}
                          className="text-indigo-600 hover:text-indigo-500 ml-1"
                        >
                          Show less
                        </button>
                      </>
                    ) : post.content.length > 300 ? (
                      <>
                        {renderContentWithMentions(post.content.substring(0, 300))}...
                        <button 
                          onClick={() => setExpandedPosts(prev => new Set([...prev, post.id]))}
                          className="text-indigo-600 hover:text-indigo-500 ml-1"
                        >
                          Read more
                        </button>
                      </>
                    ) : (
                      renderContentWithMentions(post.content)
                    )}
                  </div>
                </div>
              </div>

              {/* Post Actions */}
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-6">
                    <div className="flex items-center text-gray-500 text-sm">
                      <EyeIcon className="h-4 w-4 mr-1" />
                      {post.view_count || 0}
                    </div>
                    <button 
                      onClick={() => toggleComments(post.id)}
                      className="flex items-center text-gray-500 hover:text-indigo-600 text-sm transition-colors duration-200"
                    >
                      <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" />
                      {post.comment_count || 0}
                      {expandedComments.has(post.id) ? (
                        <ChevronUpIcon className="h-4 w-4 ml-1" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4 ml-1" />
                      )}
                    </button>
                  </div>
                  <div className="flex items-center space-x-4">
                    <PostLikes
                      postId={post.id}
                      likeCount={post.like_count}
                      userLiked={post.user_liked}
                      onToggleLike={() => handleToggleLike(post.id, post.user_liked)}
                    />
                    <PostBookmark
                      postId={post.id}
                      bookmarkCount={post.bookmark_count}
                      userBookmarked={post.user_bookmarked}
                      onToggleBookmark={handleToggleBookmark}
                    />
                    {canUserEditPost(post) && (
                      <button 
                        onClick={() => handleEditPost(post)}
                        className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                        title="Edit post"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Comments Section */}
              {expandedComments.has(post.id) && (
                <div className="border-t border-gray-200">
                  {/* Add Comment Form */}
                  <div className="p-4 bg-gray-50">
                    <div className="flex space-x-3">
                      <div className="flex-shrink-0">
                        <div className="h-8 w-8 rounded-full bg-indigo-500 flex items-center justify-center text-white text-sm font-medium">
                          U
                        </div>
                      </div>
                      <div className="flex-1">
                        <button
                          onClick={() => openCommentModal(post.id)}
                          className="w-full px-3 py-2 text-left text-gray-500 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                        >
                          Write a comment...
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Comments List */}
                  <div className="px-4 pb-4">
                    {loadingComments.has(post.id) ? (
                      <div className="flex justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {(postComments[post.id] || []).map((comment) => (
                          <div key={comment.id} className="flex space-x-3">
                            <div className="flex-shrink-0">
                              <div className="h-8 w-8 rounded-full bg-gray-400 flex items-center justify-center text-white text-sm font-medium">
                                {comment.author_name ? comment.author_name.charAt(0).toUpperCase() : 'U'}
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="bg-gray-100 rounded-lg px-3 py-2">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm font-medium text-gray-900">
                                    {comment.author_name || 'Unknown User'}
                                  </span>
                                  <div className="flex items-center space-x-2">
                                    <span className="text-xs text-gray-500">
                                      {formatCommentDate(comment.created_at)}
                                    </span>
                                    {canUserEditComment(comment) && (
                                      <button
                                        onClick={() => handleEditComment(comment)}
                                        className="text-gray-400 hover:text-indigo-600 transition-colors duration-200"
                                        title="Edit comment"
                                      >
                                        <PencilIcon className="h-4 w-4" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                                <p className="text-sm text-gray-700">{renderContentWithMentions(comment.content)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {(postComments[post.id] || []).length === 0 && !loadingComments.has(post.id) && (
                          <p className="text-center text-gray-500 text-sm py-4">
                            No comments yet. Be the first to comment!
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Load More Button */}
          {hasMore && (
            <div className="text-center">
              <button
                onClick={loadMore}
                disabled={loading}
                className="bg-white border border-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Load More Posts'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Edit Post Modal */}
      <EditPost
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingPost(null);
        }}
        onPostUpdated={handlePostUpdated}
        post={editingPost}
      />

      {/* Comment Modal */}
      <MentionModal
        isOpen={showCommentModal}
        onClose={closeCommentModal}
        onSubmit={submitComment}
        title="Write a comment"
        placeholder="Share your thoughts... You can mention users with @username or departments with @departmentname"
        submitButtonText="Post Comment"
        maxLength={2000}
      />

      {/* Edit Comment Modal */}
      <EditComment
        isOpen={showEditCommentModal}
        onClose={() => {
          setShowEditCommentModal(false);
          setEditingComment(null);
        }}
        onCommentUpdated={(updatedComment) => {
          // Find which post this comment belongs to
          let targetPostId = null;
          for (const [postId, comments] of Object.entries(postComments)) {
            if (comments.some(comment => comment.id === updatedComment.id)) {
              targetPostId = postId;
              break;
            }
          }
          
          if (targetPostId) {
            setPostComments(prev => ({
              ...prev,
              [targetPostId]: prev[targetPostId]?.map(comment =>
                comment.id === updatedComment.id ? updatedComment : comment
              ) || []
            }));
          }
          
          setShowEditCommentModal(false);
          setEditingComment(null);
        }}
        comment={editingComment}
      />
    </div>
  );
};

export default BookmarkedPosts; 